import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
  buildNotesTableDataSource,
  useDefaultFilterMenuOptions,
  useNotesTableColumns,
} from "@/entities/notes-table-v3";
import { compareDatesByDay, formatDayJs, formatStringToDayJs, isSubstringOf } from "@/shared/utils";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { useGetAllNotesQuery, useGetAllPersonsQuery } from "@/services/api";
import { useNotesRedirect } from "@/features/notes-manager";
import type { Widget } from "../types";
import type { NotesTableRowData } from "@/entities/notes-table-v3";
import { allWidgetsSelector, setAllWidgets } from "../slice";

export const useDashboardsNotesTable = ({ widgetId }: { widgetId?: string }) => {
  const { t } = useTranslation();
  const { notesTableColumnsGetters } = useNotesTableColumns();
  const dispatch = useAppDispatch();
  const widgets = useAppSelector(allWidgetsSelector);

  const [titleFilter, setTitleFilter] = useState<string>("");
  const [noteTitleFilter, setNoteTitleFilter] = useState<string>("");

  const { getDefaultNoteTypeOptions, getDefaultPersonGroupOptions } = useDefaultFilterMenuOptions();

  const currentWidget = widgets.find((widget) => widget.id === widgetId);

  const excludedCurrentWidgetWidgets = widgets.filter((widget) => widget.id !== currentWidget?.id);

  const setAllStateWidgets = (allWidgets: Widget[]) => {
    dispatch(setAllWidgets(allWidgets));
  };

  const { redirect } = useNotesRedirect();

  const onRowClick = (row: NotesTableRowData) => {
    redirect({ note: row });
  };

  const { data: notes, isLoading: notesIsLoading } = useGetAllNotesQuery();

  const { data: persons, isLoading: personsIsLoading } = useGetAllPersonsQuery();

  const isLoading = notesIsLoading || personsIsLoading;

  const setFromDateFilter = (value: string) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      { ...currentWidget, notesFilters: { ...currentWidget.notesFilters, fromDateFilter: value } },
    ]);
  };

  const setToDateFilter = (value: string) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      { ...currentWidget, notesFilters: { ...currentWidget.notesFilters, toDateFilter: value } },
    ]);
  };

  const setPersonGroupFilters = (value: string[]) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      {
        ...currentWidget,
        notesFilters: { ...currentWidget.notesFilters, personGroupFilters: value },
      },
    ]);
  };

  const setNoteTypeFilters = (value: string[]) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      {
        ...currentWidget,
        notesFilters: { ...currentWidget.notesFilters, noteTypeFilters: value },
      },
    ]);
  };

  const personGroupFilters = currentWidget?.notesFilters?.personGroupFilters;
  const fromDateFilter = currentWidget?.notesFilters?.fromDateFilter;
  const toDateFilter = currentWidget?.notesFilters?.toDateFilter;
  const noteTypeFilters = currentWidget?.notesFilters?.noteTypeFilters;

  let dataSource = buildNotesTableDataSource({
    notes: notes ?? [],
    persons: persons?.persons ?? [],
  });

  if (personGroupFilters?.length) {
    dataSource = dataSource.filter((record) => {
      const personGroup = record.person?.group;
      if (!personGroup) return false;
      return !!personGroupFilters.includes(personGroup.toUpperCase());
    });
  }

  if (titleFilter) {
    dataSource = dataSource.filter(({ person }) => {
      return !!isSubstringOf({
        sourceString: person?.title ?? "",
        searchString: titleFilter ?? "",
      });
    });
  }

  if (fromDateFilter) {
    dataSource = dataSource.filter((record) => {
      return !compareDatesByDay({
        firstDate: fromDateFilter,
        secondDate: record.noteDate,
      });
    });
  }

  if (toDateFilter) {
    dataSource = dataSource.filter((record) => {
      return !compareDatesByDay({
        firstDate: record.noteDate,
        secondDate: toDateFilter,
      });
    });
  }

  if (noteTitleFilter) {
    dataSource = dataSource.filter(({ title }) => {
      return !!isSubstringOf({
        sourceString: title ?? "",
        searchString: noteTitleFilter ?? "",
      });
    });
  }

  if (noteTypeFilters?.length) {
    dataSource = dataSource.filter((record) => {
      const noteType = record.noteType.name;
      if (!noteType) return false;
      return !!noteTypeFilters.includes(noteType);
    });
  }

  const columns = [
    notesTableColumnsGetters.getNotesTableImportantColumn({
      title: "",
      dataIndex: ["important"],
    }),
    notesTableColumnsGetters.getNotesTableNoteDateColumn({
      title: t("common.date"),
      dataIndex: ["noteDate"],
      defaultValueFilterTo: toDateFilter ? formatStringToDayJs(toDateFilter) : undefined,
      defaultValueFilterFrom: fromDateFilter ? formatStringToDayJs(fromDateFilter) : undefined,
      onChangeFilterDateFrom: (date) => setFromDateFilter(formatDayJs(date)),
      onChangeFilterDateTo: (date) => setToDateFilter(formatDayJs(date)),
    }),
    notesTableColumnsGetters.getNotesTablePersonColumn({
      title: t("common.person"),
      dataIndex: ["person"],
      menuFilters: getDefaultPersonGroupOptions().map((option) => ({
        ...option,
        defaultChecked: !!personGroupFilters?.find((item) => item === option.value),
      })),
      onChangeMenuFilters: (selected) => setPersonGroupFilters(selected),
    }),
    notesTableColumnsGetters.getNotesTablePersonTitleColumn({
      title: t("common.title"),
      dataIndex: ["person.title"],
      onChangeFilter: setTitleFilter,
      filterValue: titleFilter,
    }),
    notesTableColumnsGetters.getNotesTableNoteTypeColumn({
      title: t("common.type"),
      dataIndex: ["noteType.name"],
      menuFilters: getDefaultNoteTypeOptions().map((option) => ({
        ...option,
        defaultChecked: !!noteTypeFilters?.find((item) => item === option.value),
      })),
      onChangeMenuFilters: (selected) => setNoteTypeFilters(selected),
    }),
    notesTableColumnsGetters.getNotesTableNoteTitleColumn({
      dataIndex: ["title"],
      title: t("common.noteType.note_one"),
      onChangeFilter: setNoteTitleFilter,
      filterValue: noteTitleFilter,
    }),
  ];

  return {
    state: { columns, isLoading, dataSource },
    methods: {
      onRowClick,
    },
  };
};
